import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../store'
import {
  WQIGroupKey,
  WQIStatus,
  WQISignal,
  UICategory,
  UIStatus,
  WQIBand,
  GroupedCategory,
  UtilityReputation,
  TestResultCounts,
  SignalGroupWithStatus,
  WaterSourceType,
} from './waterQualityIndex.types'

// Base selectors
const selectWaterQualityIndexState = (state: RootState) => state.waterQualityIndex
const selectData = (state: RootState) => state.waterQualityIndex.data
const selectLoading = (state: RootState) => state.waterQualityIndex.loading
const selectError = (state: RootState) => state.waterQualityIndex.error
const selectSuccess = (state: RootState) => state.waterQualityIndex.success

// Group key to UI category mapping
const groupKeyToUICategory: Record<WQIGroupKey, UICategory> = {
  health_safety: 'Health and safety',
  home_infrastructure: 'Home infrastructure',
  skin_laundry: 'Skin, hair, laundry',
  taste_odor: 'Colour, taste, odor (CTO)',
}

// Map API status to UI status
const mapApiStatusToUIStatus = (status: WQIStatus): UIStatus => {
  switch (status) {
    case 'action':
      return 'unsafe'
    case 'monitor':
      return 'cautious'
    case 'safe':
    default:
      return 'safe'
  }
}

// Determine UI status based on score (0-100 risk scale — high = bad)
const getStatusFromScore = (score: number): UIStatus => {
  if (score >= 70) return 'unsafe'
  if (score >= 40) return 'cautious'
  return 'safe'
}

// Select outcome groups
const selectOutcomeGroups = createSelector(
  [selectData],
  (data) => data?.outcomes?.groups ?? []
)

// Select signals
const selectSignals = createSelector(
  [selectData],
  (data) => data?.signals ?? []
)

// Select utility info
const selectUtility = createSelector(
  [selectData],
  (data) => data?.utility ?? null
)

// Select recommendations
const selectRecommendations = createSelector(
  [selectData],
  (data) => data?.recommendations ?? null
)

// Overall score and band (derived from utility compliance summary or groups)
const selectOverall = createSelector(
  [selectData, selectOutcomeGroups],
  (data, groups): { score: number; band: WQIBand } | null => {
    if (!data) return null

    // Use utility compliance summary if available
    if (data.utility?.complianceSummary) {
      return {
        score: data.utility.complianceSummary.reputationScore,
        band: data.utility.complianceSummary.band,
      }
    }

    return null
  }
)

// Contributors grouped by UI category
const selectContributorsByUICategory = createSelector(
  [selectOutcomeGroups, selectSignals],
  (groups, signals): GroupedCategory[] => {
    const groupKeyOrder: WQIGroupKey[] = [
      'health_safety',
      'home_infrastructure',
      'skin_laundry',
      'taste_odor',
    ]

    return groupKeyOrder
      .map((groupKey) => {
        const group = groups.find((g) => g.groupKey === groupKey)
        if (!group) return null

        // Get signals for this group
        const groupSignals = signals.filter((s) => s.groupKey === groupKey)

        return {
          uiCategory: groupKeyToUICategory[groupKey],
          groupKey,
          status: mapApiStatusToUIStatus(group.status),
          score: group.score,
          headline: group.headline,
          summary: group.summary,
          contributors: groupSignals,
          count: groupSignals.length,
        }
      })
      .filter((group): group is GroupedCategory => group !== null && group.count > 0)
  }
)

// Water safety status (overall)
const selectWaterSafetyStatus = createSelector(
  [selectOutcomeGroups],
  (groups): UIStatus => {
    if (!groups || groups.length === 0) return 'safe'

    // Check if any group has action/unsafe status
    const hasAction = groups.some((g) => g.status === 'action')
    if (hasAction) return 'unsafe'

    // Check if any group has monitor status
    const hasMonitor = groups.some((g) => g.status === 'monitor')
    if (hasMonitor) return 'cautious'

    return 'safe'
  }
)

// Summary counts
interface SummaryCounts {
  total: number
  safe: number
  monitor: number
  actionRequired: number
}

const selectSummaryCounts = createSelector(
  [selectSignals],
  (signals): SummaryCounts => {
    const safe = signals.filter((s) => s.severity === 'safe').length
    const actionRequired = signals.filter((s) => s.severity === 'action').length
    const monitor = signals.filter((s) => s.severity === 'monitor').length

    return {
      total: signals.length,
      safe,
      monitor,
      actionRequired,
    }
  }
)

// Legacy selectors for backward compatibility
const selectCategories = createSelector(
  [selectOutcomeGroups],
  (groups) => {
    if (!groups) return null
    const categories: Record<string, number> = {}
    groups.forEach((g) => {
      const uiCategory = groupKeyToUICategory[g.groupKey]
      categories[uiCategory] = g.score
    })
    return categories
  }
)

const selectContributors = createSelector(
  [selectSignals],
  (signals) => signals
)

// Explainability (from recommendations)
const selectExplainability = createSelector(
  [selectRecommendations],
  (recommendations) => {
    if (!recommendations) return null
    return {
      whyThisScore: recommendations.mitigationPlaybook.map((p) =>
        p.steps.map((s) => s.action)
      ).flat(),
    }
  }
)

// SIPP-633: full signal object for the highest-severity parameter (Screen 2: Top Finding)
const selectTopSignal = createSelector(
  [selectSignals],
  (signals): WQISignal | null => {
    if (!signals.length) return null
    const actionSignals = signals.filter((s) => s.severity === 'action')
    if (actionSignals.length) {
      return actionSignals.reduce((a, b) => (a.score > b.score ? a : b))
    }
    const monitorSignals = signals.filter((s) => s.severity === 'monitor')
    if (monitorSignals.length) {
      return monitorSignals.reduce((a, b) => (a.score > b.score ? a : b))
    }
    return signals.reduce((a, b) => (a.score > b.score ? a : b))
  }
)

// SIPP-611: signal key of the highest-severity parameter for the event payload
const selectTopParameter = createSelector(
  [selectSignals],
  (signals): string => {
    const actionSignal = signals.find((s) => s.severity === 'action')
    if (actionSignal) return actionSignal.signalKey
    const monitorSignal = signals.find((s) => s.severity === 'monitor')
    if (monitorSignal) return monitorSignal.signalKey
    return signals[0]?.signalKey ?? ''
  }
)

// SIPP-816: Recommendations tab v2.5 selectors

// Utility reputation for the UtilityCard. Null fields mark the "no data"
// state distinctly — a real score of 100 must never collapse to null here.
const selectUtilityReputation = createSelector(
  [selectUtility],
  (utility): UtilityReputation => {
    if (!utility?.complianceSummary) {
      return { score: null, band: null, violationCount: null }
    }
    const violationCount = utility.violations?.length ?? 0
    return {
      score: utility.complianceSummary.reputationScore,
      band: utility.complianceSummary.band,
      violationCount,
    }
  }
)

// SIPP-835: source severity counts from the raw 16-parameter strip reading
// rather than WQI signal contributors. Signals are derived composites and
// can outnumber the strip's 16 actual parameters — the donut should never
// show > 16 readings.
//
// Strip results live on `state.waterTestKitResults.data[*].result.parameters`.
// The object is keyed by parameter NAME (e.g. `pH`, `Hardness`, `Nitrates`),
// each value is an array of readings, and each reading carries the strip's
// per-reading status on its `label` field (`High` / `Low` / `Caution` /
// `Good` / `NA`). Map each status to one of our four UI buckets.
const selectLatestStripParameters = (
  state: RootState
): Record<string, unknown[]> | null => {
  const items = state.waterTestKitResults?.data ?? []
  if (!items.length) return null
  const sorted = items.slice().sort((a, b) => {
    if (!a.uploadedAt || !b.uploadedAt) return 0
    if (a.uploadedAt > b.uploadedAt) return 1
    if (a.uploadedAt < b.uploadedAt) return -1
    return 0
  })
  const latest = sorted[sorted.length - 1]
  return latest?.result?.parameters ?? null
}

const selectTestResultCounts = createSelector(
  [selectLatestStripParameters],
  (parameters): TestResultCounts => {
    if (!parameters) {
      return { unsafe: 0, cautious: 0, safe: 0, notScored: 0, total: 0 }
    }

    let unsafe = 0, cautious = 0, safe = 0, notScored = 0

    for (const paramArray of Object.values(parameters)) {
      if (!Array.isArray(paramArray)) continue
      for (const reading of paramArray) {
        const label = (reading as { label?: string }).label
        if (label === 'High' || label === 'Low') unsafe++
        else if (label === 'Caution') cautious++
        else if (label === 'Good') safe++
        else if (label === 'NA') notScored++
      }
    }

    const total = unsafe + cautious + safe + notScored
    return { unsafe, cautious, safe, notScored, total }
  }
)

// Flat list of signal groups with their status and the tab route to drill into.
const selectSignalGroupsWithStatus = createSelector(
  [selectContributorsByUICategory],
  (groups): SignalGroupWithStatus[] =>
    groups.map((group) => ({
      groupName: group.uiCategory,
      status: group.status,
      tabRoute: '/profile/results',
    }))
)

// Water source type — utility if a PWSID is present, well if the well slice has
// data or a selection, otherwise unknown.
const selectWellWaterSystemsData = (state: RootState) =>
  state.wellWaterSystems?.data?.wellWaterSystems
const selectSelectedWellWaterSystem = (state: RootState) =>
  state.wellWaterSystems?.selectedWellWaterSystem

const selectWaterSourceType = createSelector(
  [selectUtility, selectWellWaterSystemsData, selectSelectedWellWaterSystem],
  (utility, wells, selectedWell): WaterSourceType => {
    if (utility?.pwsid) return 'utility'
    if (Array.isArray(wells) && wells.length > 0) return 'well'
    if (selectedWell) return 'well'
    return 'unknown'
  }
)

export const WaterQualityIndexSelectors = {
  selectState: selectWaterQualityIndexState,
  selectData,
  selectLoading,
  selectError,
  selectSuccess,
  selectOverall,
  selectCategories,
  selectContributors,
  selectContributorsByUICategory,
  selectWaterSafetyStatus,
  selectSummaryCounts,
  selectExplainability,
  // New selectors
  selectOutcomeGroups,
  selectSignals,
  selectUtility,
  selectRecommendations,
  selectTopParameter,
  selectTopSignal,
  // SIPP-816
  selectUtilityReputation,
  selectTestResultCounts,
  selectSignalGroupsWithStatus,
  selectWaterSourceType,
  // Utility functions for components
  getStatusFromScore,
  mapApiStatusToUIStatus,
  groupKeyToUICategory,
}
